/**
=========================================================
* Trust Cargo - China suppliers
=========================================================

* Two questions, one screen:
*   1. what do we owe each supplier
*   2. where has each shipment got to on its way to the China warehouse
*
* Tab 1 is the account list. Tab 2 is the tracking board, filterable by
* customer - which is the case this was built for: one customer buying from
* ten suppliers at once.
*/

import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import axios from "axios";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import { FilterSelect, optionsFromRows, matches } from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import IdCell, { IdHead } from "components/IdCell";
import AddDialog from "components/AddDialog";
import RowActions from "components/RowActions";
import SupplierPaymentsDialog from "components/SupplierPaymentsDialog";
import SupplierPaymentDialog from "components/SupplierPaymentDialog";
import withOrderTotals from "utils/supplierOrderTotals";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api/v2";

const money = (v) =>
  v === null || v === undefined || v === "" ? "-" : Number(v).toLocaleString("en-US");

// the four stages, in the order goods actually move through them
const STAGES = [
  { key: "ordered_at", label: "Ordered" },
  { key: "paid_at", label: "Paid" },
  { key: "shipped_at", label: "Shipped" },
  { key: "received_at", label: "At warehouse" },
];

const VIEWS = [
  { key: "accounts", label: "Supplier accounts" },
  { key: "tracking", label: "Tracking" },
];

function Suppliers() {
  const [view, setView] = useState("accounts");
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // which dialog is open: {entity, recordId} or null
  const [dialog, setDialog] = useState(null);
  // the supplier whose payment history is open, or null
  const [history, setHistory] = useState(null);
  // the record-payment dialog
  const [paying, setPaying] = useState(false);
  // account filters (accounts tab) and purchase filters (tracking tab)
  const [owing, setOwing] = useState("");
  const [city, setCity] = useState("");
  const [customer, setCustomer] = useState("");
  const [supplierPick, setSupplierPick] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const [s, p] = await Promise.all([
        fetch(`${API}/suppliers`, { cache: "no-store" }),
        fetch(`${API}/purchases?limit=1000`, { cache: "no-store" }),
      ]);

      if (!s.ok || !p.ok) {
        // most likely cause: the supplier tables have not been created yet
        setError(`Supplier data unavailable (HTTP ${!s.ok ? s.status : p.status})`);
        setSuppliers([]);
        setPurchases([]);
      } else {
        setSuppliers(await withOrderTotals(await s.json()));
        setPurchases(await p.json());
      }
    } catch (e) {
      setError(e?.message || "Network error");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const source = view === "accounts" ? suppliers : purchases;

  const stageFiltered =
    view === "tracking" && stageFilter !== "all"
      ? purchases.filter((p) => (stageFilter === "at_warehouse" ? p.received_at : !p.received_at))
      : source;

  /**
   * Each tab filters on its own columns: an account has a balance and a
   * city, a purchase has a customer and a supplier.
   */
  const narrowed = stageFiltered.filter((r) => {
    if (view === "accounts") {
      if (!matches(r.city, city)) return false;
      if (owing === "owed" && !(Number(r.orders_balance ?? r.balance) > 0)) return false;
      if (owing === "settled" && Number(r.orders_balance ?? r.balance) > 0) return false;
      return true;
    }

    return matches(r.customer, customer) && matches(r.supplier, supplierPick);
  });

  const filteredRows = filterRows(narrowed, query);
  const pager = usePagination(filteredRows);

  const owed = suppliers.reduce((s, x) => s + (Number(x.balance) || 0), 0);
  const notArrived = purchases.filter((p) => !p.received_at).length;

  /** Stamp a stage on a purchase and refresh. */
  const markStage = async (row, stageKey) => {
    setBusy(true);
    try {
      // clicking the stage it is already on clears it, so a mis-click is
      // recoverable without a separate "undo" control
      const already = Boolean(row[stageKey]);
      await axios.post(`${API}/purchases/${row.id}/stage`, {
        stage: stageKey,
        clear: already,
      });
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not update stage");
    }
    setBusy(false);
  };

  /** Delete a supplier or a purchase, with the API's reason surfaced. */
  const remove = async (kind, row) => {
    const what =
      kind === "suppliers"
        ? `supplier ${row.code} - ${row.name}`
        : `purchase ${row.invoice_no || row.id}`;

    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${what}?`)) return;

    setBusy(true);
    try {
      await axios.delete(`${API}/${kind}/${row.id}`);
      await fetchData();
    } catch (e) {
      // the API refuses to delete a supplier with an open balance, and says so
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
    setBusy(false);
  };

  const cell = { fontSize: 14, whiteSpace: "nowrap" };

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <Card>
          {/* title, view switch, stage filters, search, totals and both
              buttons - one row */}
          <FilterBar
            title="Suppliers"
            value={query}
            onChange={setQuery}
            count={filteredRows.length}
            total={stageFiltered.length}
            placeholder={
              view === "accounts"
                ? "Search supplier by code, name or city..."
                : "Search by supplier, customer, invoice or tracking no..."
            }
            actions={
              <>
                <SuiTypography variant="caption" sx={{ opacity: 0.75, whiteSpace: "nowrap" }}>
                  {`Owed ${money(owed)} · ${notArrived} in transit`}
                </SuiTypography>
                <Button variant="outlined" color="primary" onClick={() => setPaying(true)}>
                  Record Payment
                </Button>
                {/* the primary action follows whichever tab you are on */}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    setDialog({ entity: view === "accounts" ? "supplier" : "purchase" })
                  }
                >
                  {view === "accounts" ? "Add Supplier" : "Add Purchase"}
                </Button>
              </>
            }
          >
            {view === "accounts" ? (
              <>
                <FilterSelect
                  label="Balance"
                  icon="payments"
                  value={owing}
                  onChange={setOwing}
                  anyLabel="All"
                  options={[
                    { value: "owed", label: "Still owed" },
                    { value: "settled", label: "Settled" },
                  ]}
                />
                <FilterSelect
                  label="City"
                  icon="place"
                  value={city}
                  onChange={setCity}
                  options={optionsFromRows(suppliers, "city")}
                />
              </>
            ) : (
              <>
                <FilterSelect
                  label="Customer"
                  icon="person"
                  value={customer}
                  onChange={setCustomer}
                  options={optionsFromRows(purchases, "customer")}
                />
                <FilterSelect
                  label="Supplier"
                  icon="local_shipping"
                  value={supplierPick}
                  onChange={setSupplierPick}
                  options={optionsFromRows(purchases, "supplier")}
                />
              </>
            )}

            {VIEWS.map((v) => (
              <Chip
                key={v.key}
                size="small"
                label={v.label}
                onClick={() => {
                  setView(v.key);
                  setQuery("");
                }}
                sx={({ palette, functions: { rgba } }) => ({
                  fontWeight: view === v.key ? 700 : 500,
                  cursor: "pointer",
                  backgroundColor:
                    view === v.key ? rgba(palette.primary.main, 0.18) : "transparent",
                  border: `1px solid ${rgba(palette.dark.main, 0.15)}`,
                })}
              />
            ))}

            {view === "tracking" &&
              ["all", "in_transit", "at_warehouse"].map((k) => (
                <Chip
                  key={k}
                  size="small"
                  label={k.replace("_", " ")}
                  onClick={() => setStageFilter(k)}
                  sx={({ palette, functions: { rgba } }) => ({
                    cursor: "pointer",
                    fontWeight: stageFilter === k ? 700 : 500,
                    backgroundColor:
                      stageFilter === k ? rgba(palette.info.main, 0.16) : "transparent",
                    border: `1px solid ${rgba(palette.dark.main, 0.12)}`,
                  })}
                />
              ))}

            {error && (
              <SuiTypography variant="caption" color="error" fontWeight="bold">
                {error}
              </SuiTypography>
            )}
          </FilterBar>

          {loading ? (
            <CargoLoader label="Loading suppliers" />
          ) : (
            <>
              <SuiBox sx={{ overflowX: "auto" }}>
                {view === "accounts" ? (
                  <Table sx={{ minWidth: 1050 }}>
                    <TableHead>
                      <TableRow>
                        <IdHead />
                        <TableCell align="center">Code</TableCell>
                        <TableCell align="center">Supplier</TableCell>
                        <TableCell align="center">City</TableCell>
                        <TableCell align="center">Contact</TableCell>
                        <TableCell align="center">Invoiced</TableCell>
                        <TableCell align="center">Paid</TableCell>
                        <TableCell align="center">Owed</TableCell>
                        {/* from the customer orders carrying this code, in
                            the order the figures happen: the quoted one
                            first, then the agreed one */}
                        <TableCell align="center">Expected Balance</TableCell>
                        <TableCell align="center">Balance</TableCell>
                        <TableCell align="center">Operations</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pager.pageRows.map((s) => (
                        <TableRow key={s.id}>
                          <IdCell value={s.id} />
                          <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                            {s.code}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {s.name}
                            {s.name_cn ? ` (${s.name_cn})` : ""}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {s.city || "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {s.phone || s.wechat || "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {money(s.invoiced)}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {money(s.paid)}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={({ palette }) => ({
                              ...cell,
                              fontWeight: 700,
                              color: Number(s.balance) > 0 ? palette.error.main : palette.dark.main,
                            })}
                          >
                            {money(s.balance)}
                          </TableCell>

                          {/* first_amount of this code's orders, less what has
                              been paid out - the figure quoted up front */}
                          <TableCell align="center" sx={{ ...cell, opacity: 0.85 }}>
                            {s.orders_expected_balance === null ||
                            s.orders_expected_balance === undefined
                              ? "-"
                              : money(s.orders_expected_balance)}
                          </TableCell>

                          {/* the same against final_amount - what is actually
                              owed once the amount was agreed */}
                          <TableCell
                            align="center"
                            sx={({ palette }) => ({
                              ...cell,
                              fontWeight: 700,
                              color:
                                Number(s.orders_balance) > 0
                                  ? palette.error.main
                                  : palette.success.main,
                            })}
                          >
                            {money(s.orders_balance)}
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            <RowActions
                              actions={[
                                {
                                  icon: "receipt_long",
                                  label: "All payments for this supplier",
                                  tone: "info",
                                  onClick: () => setHistory({ id: s.id, name: s.name }),
                                },
                                {
                                  icon: "edit",
                                  label: "Edit supplier",
                                  onClick: () => setDialog({ entity: "supplier", recordId: s.id }),
                                },
                                {
                                  icon: "delete",
                                  label: "Delete supplier",
                                  tone: "danger",
                                  disabled: busy,
                                  onClick: () => remove("suppliers", s),
                                },
                              ]}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                            <SuiTypography variant="button" color="text">
                              No suppliers
                            </SuiTypography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                ) : (
                  <Table sx={{ minWidth: 1150 }}>
                    <TableHead>
                      <TableRow>
                        <IdHead />
                        <TableCell align="center">Supplier</TableCell>
                        <TableCell align="center">Customer</TableCell>
                        <TableCell align="center">Shop code</TableCell>
                        <TableCell align="center">Invoice</TableCell>
                        <TableCell align="center">Goods</TableCell>
                        <TableCell align="center">Amount</TableCell>
                        <TableCell align="center">CTN</TableCell>
                        <TableCell align="center">Progress</TableCell>
                        <TableCell align="center">Stages</TableCell>
                        <TableCell align="center">Operations</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pager.pageRows.map((p) => (
                        <TableRow key={p.id}>
                          <IdCell value={p.id} />
                          <TableCell align="center" sx={cell}>
                            {p.supplier}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {p.customer}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {p.shop_code || "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {p.invoice_no || "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {p.description || "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {`${money(p.amount)} ${p.currency}`}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {p.ctn}
                          </TableCell>

                          <TableCell align="center" sx={{ ...cell, minWidth: 150 }}>
                            <SuiBox display="flex" flexDirection="column" gap={0.5}>
                              <SuiTypography variant="caption" fontWeight="bold">
                                {p.stage}
                              </SuiTypography>
                              <LinearProgress
                                variant="determinate"
                                value={p.progress}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </SuiBox>
                          </TableCell>

                          {/* click a stage to stamp it; click it again to clear */}
                          <TableCell align="center" sx={cell}>
                            <SuiBox display="flex" gap={0.5} justifyContent="center">
                              {STAGES.map((st) => {
                                const done = Boolean(p[st.key]);
                                return (
                                  <Chip
                                    key={st.key}
                                    size="small"
                                    label={st.label}
                                    onClick={() => !busy && markStage(p, st.key)}
                                    title={done ? p[st.key] : "not yet"}
                                    sx={({ palette, functions: { rgba } }) => ({
                                      cursor: busy ? "default" : "pointer",
                                      fontWeight: done ? 700 : 500,
                                      fontSize: 11,
                                      backgroundColor: done
                                        ? rgba(palette.primary.main, 0.22)
                                        : rgba(palette.dark.main, 0.05),
                                      color: done
                                        ? palette.dark.main
                                        : rgba(palette.dark.main, 0.55),
                                    })}
                                  />
                                );
                              })}
                            </SuiBox>
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            <RowActions
                              actions={[
                                {
                                  icon: "edit",
                                  label: "Edit purchase",
                                  onClick: () => setDialog({ entity: "purchase", recordId: p.id }),
                                },
                                {
                                  icon: "delete",
                                  label: "Delete purchase",
                                  tone: "danger",
                                  disabled: busy,
                                  onClick: () => remove("purchases", p),
                                },
                              ]}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                            <SuiTypography variant="button" color="text">
                              No purchases
                            </SuiTypography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </SuiBox>
              <Pagination {...pager} />
            </>
          )}
        </Card>
      </SuiBox>

      {/* one dialog serves add and edit for suppliers, purchases and payments */}
      <AddDialog
        open={Boolean(dialog)}
        entity={dialog?.entity || "supplier"}
        recordId={dialog?.recordId || null}
        onClose={() => setDialog(null)}
        onCreated={fetchData}
      />

      <SupplierPaymentDialog open={paying} onClose={() => setPaying(false)} onSaved={fetchData} />

      {/* every payment made to one supplier, over the list */}
      <SupplierPaymentsDialog
        open={Boolean(history)}
        supplierId={history?.id}
        supplierName={history?.name}
        onClose={() => setHistory(null)}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Suppliers;
